<template>
  <v-dialog
    v-model="dialogShown"
    width="25vw"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        :class="'ml-4 zamg-button' + (outlined ? ' zamg-button-secondary' : '')"
        :data-testid="testid"
        color="primary"
        :disabled="disabled"
        small
        v-on="on"
        @click="dialogShown = true"
      >
        <v-icon class="download-icon">
          mdi-download
        </v-icon>
        {{ title }}
      </v-btn>
    </template>
    <v-card>
      <v-card-title
        :data-testid="`${testid}-card-title`"
        class="text-uppercase zamg-text-continuous"
      >
        {{ title + ' ' + t('download_form.to_download') }}
      </v-card-title>
      <v-card-text>
        <text-field-with-date-picker
          v-if="showDatePicker"
          :value="startDate"
          :label="t('start_date')"
          :testid="'download-dialog-start-date'"
          :allowed-date-range="[
            datasetStore.metadata?.startDate,
            datasetStore.metadata?.endDate,
          ]"
          :show-hint="true"
          :text-field-props="{
            ...datePickerTextFieldProps,
            filled: true,
          }"
          :selected-date-is-start-date="true"
          :reference-date="endDate"
          @date-selected="setStartDate"
        />
        <text-field-with-date-picker
          v-if="showDatePicker"
          :value="endDate"
          :label="t('end_date')"
          :testid="'download-dialog-end-date'"
          :allowed-date-range="[
            datasetStore.metadata?.startDate,
            datasetStore.metadata?.endDate,
          ]"
          :show-hint="true"
          :text-field-props="{
            ...datePickerTextFieldProps,
            filled: true,
          }"
          :selected-date-is-start-date="false"
          :reference-date="startDate"
          @date-selected="setEndDate"
        />
        <v-checkbox
          v-if="showDatePicker"
          v-model="addDownloadRangeToFilename"
          class="mb-4 ml-2 mt-0 pt-0"
          :disabled="startDate === null || endDate === null"
          :hide-details="true"
          density="compact"
        >
          <template #label>
            <span
              id="checkboxLabel"
              data-testid="add-download-range-to-filename"
              :class="
                'zamg-text-small-continuous' +
                  (startDate === null || endDate === null
                    ? ' zamg-text-disabled'
                    : ' zamg-text-input')
              "
            >
              {{ t('download_form.add_download_range') }}
            </span>
          </template>
        </v-checkbox>
        <v-text-field
          v-model="fileName"
          class="mb-3"
          :label="t('download_form.download_file_name')"
          variant="filled"
          persistent-hint
          :data-testid="`${testid}-download-file-name-input`"
          :hint="'overridden by slot'"
          @input="(event) => onUserInput(event.target.value)"
        >
          <template #message>
            <span
              v-if="fileName !== ''"
              :data-testid="`${testid}-download-file-name-label`"
              class="zamg-text-input"
            >
              {{ t('download_form.file_name_hint') }}:
            </span>
            <span :data-testid="`${testid}-download-file-name-result`">
              {{
                fileName === ''
                  ? t('download_form.file_name_error_message')
                  : resultingFilename
              }}
            </span>
          </template>
        </v-text-field>
        <v-select
          v-model="selectedFormat"
          :items="formatChoices"
          :label="t('format')"
          hide-details
          variant="filled"
        />
      </v-card-text>
      <v-card-actions class="pb-4 px-6">
        <v-spacer />
        <v-btn
          color="primary"
          variant="outlined"
          @click="closeDialog"
        >
          {{ t('abort') }}
        </v-btn>
        <v-btn
          class="ml-4 primary"
          color="primary"
          variant="elevated"
          :data-testid="`${testid}-download-data`"
          :disabled="
            fileName === '' ||
              ((startDate === null || endDate === null) && showDatePicker) ||
              disableDownloadButton
          "
          @click="triggerDownload"
        >
          {{ t('download_form.to_download') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import TextFieldWithDatePicker from '@/components/common/TextFieldWithDatePicker.vue';
import DatasetStore from '@/stores/dataset/dataset-module';
import { Frequency, formatDateForFilename } from '@/util/datetime';
import { formatChoice } from '@/util/formatOptions';
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: 'DownloadButtonWithPopUp',
  components: {
    TextFieldWithDatePicker,
  },
  props: {
    title: { type: String, required: true },
    formatChoices: { type: Array as () => formatChoice[], required: true },
    testid: { type: String, required: true },
    outlined: { type: Boolean, default: false },
    defaultStartDate: { type: Date, required: false, default: null },
    defaultEndDate: { type: Date, required: false, default: null},
    showDatePicker: { type: Boolean, default: false },
    datePickerTextFieldProps: {
      type: Object as () => Record<string, any>,
      default: () => ({}),
    },
    fileSuffix: { type: String, default: '' },
    disableDownloadButton: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ['download'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const datasetStore = DatasetStore;
    const freq = datasetStore.metadata!.frequency as Frequency;
    const dialogShown = ref(false);
    const selectedFormat = ref(getDefaultFormatChoice());
    const addDownloadRangeToFilename = ref(true);
    const startDate = ref<Date | null>(null);
    const endDate = ref<Date | null>(null);

    const defaultFileName = computed(() =>
      (datasetStore.metadata?.title ?? '') + props.fileSuffix
    );
    const fileName = ref(defaultFileName.value);
    const userEdited = ref(false);

    function getDefaultFormatChoice(): string {
      if (props.formatChoices.length > 0) {
        return props.formatChoices[0].value;
      } else {
        return 'json';
      }
    }

    const resultingFilename = computed(() => {
      if (
        addDownloadRangeToFilename.value &&
        startDate.value !== null &&
        endDate.value !== null
      ) {
        const startDateIso = formatDateForFilename(startDate.value, freq);
        const endDateIso = formatDateForFilename(endDate.value, freq);
        return `${fileName.value}_${startDateIso}_${endDateIso}.${selectedFormat.value}`;
      } else {
        return `${fileName.value}.${selectedFormat.value}`;
      }
    });

    watch(
      () => props.defaultStartDate,
      (newVal) => {
        startDate.value = newVal === undefined ? null : newVal;
      },
      { immediate: true }
    );

    watch(
      () => props.defaultEndDate,
      (newVal) => {
        endDate.value = newVal === undefined ? null : newVal;
      },
      { immediate: true }
    );

    watch(defaultFileName, (newName) => {
      if (!userEdited.value) {
        fileName.value = newName;
      }
    });

    function setStartDate(value: Date | null) {
      startDate.value = value;
    }

    function setEndDate(value: Date | null) {
      endDate.value = value;
    }

    function closeDialog() {
      dialogShown.value = false;
    }

    function triggerDownload() {
      closeDialog();
      emit('download', {
        fileName: resultingFilename.value,
        downloadRange: [startDate.value, endDate.value] as [Date | null, Date | null],
      });
    }

    function onUserInput(value: string) {
      fileName.value = value;
      userEdited.value = true;
    }

    return {
      t,
      dialogShown,
      selectedFormat,
      fileName,
      addDownloadRangeToFilename,
      startDate,
      endDate,
      datasetStore,
      freq,
      onUserInput,
      resultingFilename,
      setStartDate,
      setEndDate,
      closeDialog,
      triggerDownload,
    };
  },
});
</script>
<style scoped>
.download-icon {
  color: rgb(var(--v-theme-on-primary));
}
</style>
